import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { LetterPreview } from "./LetterPreview";
import { LetterPdfDocument } from "./LetterPdfDocument";
import { JournalistOption, Language, DispatchChannel } from "./types";

const TEN_YEARS_SECONDS = 60 * 60 * 24 * 365 * 10;

const schema = z.object({
  journalist_id: z.string().min(1, "Select a journalist"),
  language: z.enum(["en", "fr", "pt"]),
  event_name: z.string().optional(),
  event_dates: z.string().optional(),
  event_venue: z.string().optional(),
  signed_by_name: z.string().min(1, "Required"),
  signed_by_title: z.string().min(1, "Required"),
  dispatch_channel: z.enum(["email", "pdf_download", "both"]),
});

type FormValues = z.infer<typeof schema>;

interface LetterComposeSheetProps {
  open: boolean;
  onClose: () => void;
  onDispatched: () => void;
  journalists: JournalistOption[];
  orgId: string;
  commsEmail: string;
  currentUserName: string;
  currentUserTitle: string;
}

export function LetterComposeSheet({
  open,
  onClose,
  onDispatched,
  journalists,
  orgId,
  commsEmail,
  currentUserName,
  currentUserTitle,
}: LetterComposeSheetProps) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [jPopoverOpen, setJPopoverOpen] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors }, reset } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        journalist_id: "",
        language: "fr",
        event_name: "",
        event_dates: "",
        event_venue: "",
        signed_by_name: currentUserName,
        signed_by_title: currentUserTitle,
        dispatch_channel: "email",
      },
    });

  useEffect(() => {
    if (open) {
      reset({
        journalist_id: "",
        language: "fr",
        event_name: "",
        event_dates: "",
        event_venue: "",
        signed_by_name: currentUserName,
        signed_by_title: currentUserTitle,
        dispatch_channel: "email",
      });
    }
  }, [open, currentUserName, currentUserTitle, reset]);

  const values = watch();
  const selectedJournalist = journalists.find((j) => j.id === values.journalist_id);

  async function onSubmit(data: FormValues) {
    setSubmitting(true);
    try {
      // 1. Call generate-invitation-letter edge function
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        "generate-invitation-letter",
        {
          body: {
            journalist_id: data.journalist_id,
            language: data.language,
            signed_by_name: data.signed_by_name,
            signed_by_title: data.signed_by_title,
            event_name: data.event_name || undefined,
            event_dates: data.event_dates || undefined,
            event_venue: data.event_venue || undefined,
            dispatch_channel: data.dispatch_channel,
          },
        }
      );

      if (fnError || !fnData?.success) {
        throw new Error(fnData?.error ?? fnError?.message ?? "Generation failed");
      }

      // 2. Render the actual PDF client-side and upload it to storage
      const ld = fnData.letter_data;
      const blob = await pdf(
        <LetterPdfDocument
          language={ld.language}
          firstName={ld.first_name}
          lastName={ld.last_name}
          outletName={ld.outlet_name}
          gender={selectedJournalist?.gender ?? "undisclosed"}
          commsEmail={ld.comms_email || "communication@parl.ecowas.int"}
          signedByName={ld.signed_by_name}
          signedByTitle={ld.signed_by_title}
          eventName={ld.event_name || undefined}
          eventDates={ld.event_dates || undefined}
          eventVenue={ld.event_venue || undefined}
          attendanceCode={ld.attendance_code || undefined}
          attendanceType={ld.attendance_type || undefined}
        />
      ).toBlob();

      const { error: uploadError } = await supabase.storage
        .from("invitation-pdfs")
        .upload(fnData.file_path, blob, { contentType: "application/pdf", upsert: true });
      if (uploadError) throw new Error(`PDF upload failed: ${uploadError.message}`);

      const { data: signedData, error: signError } = await supabase.storage
        .from("invitation-pdfs")
        .createSignedUrl(fnData.file_path, TEN_YEARS_SECONDS);
      if (signError || !signedData?.signedUrl) {
        throw new Error(`Could not sign PDF URL: ${signError?.message ?? "unknown error"}`);
      }

      await supabase
        .from("invitation_letters" as any)
        .update({ pdf_url: signedData.signedUrl })
        .eq("id", fnData.letter_id);

      fnData.pdf_url = signedData.signedUrl;

      // 3. If dispatch includes email, send the invitation email
      if (
        data.dispatch_channel === "email" ||
        data.dispatch_channel === "both"
      ) {
        const { data: sendData } = await supabase.functions.invoke(
          "send-accreditation-invite",
          {
            body: {
              journalist_id: data.journalist_id,
              letter_id: fnData.letter_id,
              language: data.language,
            },
          }
        );
        if (!sendData?.success) {
          // Non-fatal, letter was created; email delivery failed
          toast({
            title: "Letter created, email delivery failed",
            description: "Download the PDF and send manually.",
            variant: "destructive",
          });
        }
      }

      toast({
        title: `Letter created: ${fnData.reference_number}`,
        description:
          data.dispatch_channel !== "pdf_download"
            ? `Email sent to ${selectedJournalist?.email ?? "journalist"}`
            : "Download the PDF from the letters table.",
      });

      // 3. If dispatch_channel is pdf_download or both, open PDF URL
      if (
        (data.dispatch_channel === "pdf_download" ||
          data.dispatch_channel === "both") &&
        fnData.pdf_url
      ) {
        window.open(fnData.pdf_url, "_blank");
      }

      onDispatched();
      onClose();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] max-w-[1400px] h-[92vh] max-h-[92vh] overflow-y-auto">
        <DialogHeader className="mb-6">
          <DialogTitle>New Invitation Letter</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left, form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Journalist selector */}
            <div className="space-y-1.5">
              <Label>Journalist</Label>
              <Popover open={jPopoverOpen} onOpenChange={setJPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                  >
                    {selectedJournalist
                      ? `${selectedJournalist.first_name} ${selectedJournalist.last_name}`
                      : "Select journalist…"}
                    <ChevronsUpDown className="ml-2 w-4 h-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[380px] p-0">
                  <Command>
                    <CommandInput placeholder="Search…" />
                    <CommandList>
                      <CommandEmpty>No journalist found.</CommandEmpty>
                      <CommandGroup>
                        {journalists.map((j) => (
                          <CommandItem
                            key={j.id}
                            value={`${j.first_name} ${j.last_name} ${j.outlet_name ?? ""}`}
                            onSelect={() => {
                              setValue("journalist_id", j.id);
                              setJPopoverOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 w-4 h-4",
                                values.journalist_id === j.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            <div>
                              <div className="font-medium">
                                {j.first_name} {j.last_name}
                              </div>
                              {j.outlet_name && (
                                <div className="text-xs text-muted-foreground">
                                  {j.outlet_name}
                                </div>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.journalist_id && (
                <p className="text-justify text-xs text-destructive">
                  {errors.journalist_id.message}
                </p>
              )}
            </div>

            {/* Language */}
            <div className="space-y-1.5">
              <Label>Language</Label>
              <RadioGroup
                value={values.language}
                onValueChange={(v) => setValue("language", v as Language)}
                className="flex gap-4"
              >
                {(["fr", "en", "pt"] as Language[]).map((lang) => (
                  <div key={lang} className="flex items-center gap-2">
                    <RadioGroupItem value={lang} id={`lang-${lang}`} />
                    <Label htmlFor={`lang-${lang}`} className="cursor-pointer">
                      {lang === "fr" ? "🇫🇷 FR" : lang === "en" ? "🇬🇧 EN" : "🇵🇹 PT"}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Event details (optional) */}
            <div className="space-y-3 rounded-lg border p-4">
              <p className="text-justify text-sm font-medium">
                Event Details{" "}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="event_name">Event Name</Label>
                <Input
                  id="event_name"
                  {...register("event_name")}
                  placeholder="e.g. 4th Ordinary Session 2026"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="event_dates">Event Dates</Label>
                <Input
                  id="event_dates"
                  {...register("event_dates")}
                  placeholder="e.g. 15 au 19 juin 2026"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="event_venue">Venue</Label>
                <Input
                  id="event_venue"
                  {...register("event_venue")}
                  placeholder="e.g. Dakar, Sénégal"
                />
              </div>
            </div>

            {/* Signatory */}
            <div className="space-y-3 rounded-lg border p-4">
              <p className="text-justify text-sm font-medium">Signatory</p>
              <div className="space-y-1.5">
                <Label htmlFor="signed_by_name">Name</Label>
                <Input
                  id="signed_by_name"
                  {...register("signed_by_name")}
                  placeholder="Full name of signatory"
                />
                {errors.signed_by_name && (
                  <p className="text-justify text-xs text-destructive">
                    {errors.signed_by_name.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signed_by_title">Title</Label>
                <Input
                  id="signed_by_title"
                  {...register("signed_by_title")}
                  placeholder="e.g. Head of Communication Division"
                />
                {errors.signed_by_title && (
                  <p className="text-justify text-xs text-destructive">
                    {errors.signed_by_title.message}
                  </p>
                )}
              </div>
            </div>

            {/* Dispatch method */}
            <div className="space-y-1.5">
              <Label>Dispatch Method</Label>
              <RadioGroup
                value={values.dispatch_channel}
                onValueChange={(v) =>
                  setValue("dispatch_channel", v as DispatchChannel)
                }
                className="space-y-2"
              >
                {[
                  { value: "email", label: "Email only" },
                  { value: "pdf_download", label: "Download PDF only" },
                  { value: "both", label: "Email + Download PDF" },
                ].map((opt) => (
                  <div key={opt.value} className="flex items-center gap-2">
                    <RadioGroupItem value={opt.value} id={`dc-${opt.value}`} />
                    <Label htmlFor={`dc-${opt.value}`} className="cursor-pointer">
                      {opt.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="pt-4 border-t flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                style={{ backgroundColor: "#006B3C" }}
                className="text-white hover:opacity-90"
              >
                {submitting && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Generate &amp; Dispatch
              </Button>
            </div>
          </form>

          {/* Right, live preview */}
          <div className="border rounded-lg overflow-auto max-h-[80vh] bg-white">
            <div className="px-3 py-2 bg-muted text-xs font-medium text-muted-foreground border-b">
              Letter Preview
            </div>
            <div className="transform scale-[0.55] origin-top-left w-[181%]">
              <LetterPreview
                language={values.language as Language}
                firstName={selectedJournalist?.first_name ?? "First Name"}
                lastName={selectedJournalist?.last_name ?? "Last Name"}
                outletName={selectedJournalist?.outlet_name ?? "Media Outlet"}
                gender={selectedJournalist?.gender ?? "undisclosed"}
                commsEmail={commsEmail || "communication@parl.ecowas.int"}
                signedByName={values.signed_by_name || "Signatory Name"}
                signedByTitle={values.signed_by_title || "Title"}
                eventName={values.event_name || undefined}
                eventDates={values.event_dates || undefined}
                eventVenue={values.event_venue || undefined}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
